import { useCallback, useEffect, useRef } from "react";

interface DebouncedCallback<A extends unknown[]> {
  /** Schedules the callback, replacing any pending call. */
  run: (...args: A) => void;
  /** Drops a pending call without running it. */
  cancel: () => void;
}

/**
 * Debounces a callback: `run` fires it after `delayMs` of quiet, and each call
 * resets the timer. `cancel` drops a pending call (e.g. before an action that
 * supersedes it), and unmounting cancels automatically. The latest `callback`
 * is always used, so `run`/`cancel` keep a stable identity.
 */
export function useDebouncedCallback<A extends unknown[]>(
  callback: (...args: A) => void,
  delayMs: number,
): DebouncedCallback<A> {
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  const cancel = useCallback(() => clearTimeout(timer.current), []);
  useEffect(() => cancel, [cancel]);

  const run = useCallback(
    (...args: A) => {
      clearTimeout(timer.current);
      timer.current = setTimeout(() => callbackRef.current(...args), delayMs);
    },
    [delayMs],
  );

  return { run, cancel };
}
